import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Ban, Loader2, Droplets, Wind, Calendar, AlertCircle, CheckCircle2, History, RefreshCw, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { fetchAbortedWashingCycles, createAbortedWashingCycle } from '@/pages/washingCycles/services/washingCycles.services';
import { fetchAbortedDryingCycles, createAbortedDryingCycle } from '@/pages/dryingCycles/services/dryingCycles.services';
import { motion, AnimatePresence } from 'framer-motion';

const ABORT_REASONS = [
  'Mantenimiento',
  'Problemas eléctricos',
  'Error Máquina',
  'Programa mal seleccionado',
  'Otro',
];

/**
 * Devuelve la fecha de HOY en zona horaria local en formato YYYY-MM-DD.
 * Se usa SOLO para registrar ciclos abortados (lavado o secado), ya que
 * el operador los registra el mismo día en que ocurren.
 *
 * Importante: se usan SIEMPRE métodos locales (getFullYear, getMonth,
 * getDate) para evitar el desfase de un día que se produce al usar
 * `toISOString()` cerca del cambio de jornada en zonas horarias al
 * oeste de UTC (p. ej. Colombia UTC-5): allí, después de las 19:00
 * local `toISOString()` ya devuelve el día SIGUIENTE en UTC.
 */
const getAbortedCycleDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Estado inicial del formulario.
 * `fecha` se inicializa con HOY (en local) en vez de string vacío para
 * que el input controlado siempre tenga un valor válido y la fecha
 * que el usuario selecciona (o acepta) sea la misma que se envía al
 * backend. Si se inicializa en '' y el operador deja la fecha por
 * defecto, el `value` del input se recalculaba en cada render
 * mezclando estado vs. helper y podía reescribir la fecha elegida.
 */
const getDefaultForm = () => ({
  type: 'lavado',
  machineId: '',
  ciclos: '',
  razon: '',
  otraRazon: '',
  fecha: getAbortedCycleDate(),
});

/**
 * Formatea una fecha ISO a "DD/MM/YYYY HH:mm" en zona local.
 */
const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Devuelve la fecha OPERATIVA del registro, es decir, la que el
 * operador seleccionó al registrar el ciclo (`fecha`). Sólo si el
 * backend no la trae (caso raro) recurrimos a `createdAt`.
 *
 * Esto es importante para que la lista de "últimos registros" no
 * muestre el "momento de creación" (createdAt, que siempre es hoy)
 * cuando el operador estaba registrando, por ejemplo, un ciclo de
 * la jornada anterior.
 */
const getRecordDate = (r) => r?.fecha || r?.createdAt;

/**
 * Cuántos registros mostramos por página en la columna derecha del modal.
 * Como los registros vienen de DOS endpoints distintos (washing + drying) y
 * el orden entre páginas se hace en el cliente, mantenemos un set "grande"
 * en memoria y paginamos localmente. 5 por página es cómodo para revisar
 * visualmente en una columna estrecha y permite navegar la historia sin
 * pedir más al backend.
 */
const RECENT_PAGE_SIZE = 5;
/**
 * Límite por endpoint al pedir el histórico. Con 200 + 200 tenemos 400
 * registros en el peor caso, suficiente para un día operativo normal
 * de cualquier lavandería. Si se requiere más, basta subir este número.
 */
const RECENT_FETCH_LIMIT = 200;

/**
 * Modal para registrar ciclos abortados (lavado o secado) en una lavandería.
 *
 * Layout de 2 columnas:
 *   - Izquierda: formulario de registro
 *   - Derecha: lista de los últimos ciclos abortados del centro (lavado + secado)
 *
 * Props:
 *   - isOpen: boolean
 *   - onClose: () => void
 *   - machines: Lavadoras de la lavandería actual
 *   - dryers: Secadoras de la lavandería actual
 *   - idLavanderia: ID del centro
 *   - laundryName: Nombre del centro (para mostrar en el payload)
 *   - loadingDryers: boolean (cargando secadoras)
 *   - onCycleCreated: () => void  // callback opcional tras un guardado exitoso
 */
const AbortedCyclesModal = ({
  isOpen,
  onClose,
  machines = [],
  dryers = [],
  idLavanderia,
  laundryName,
  onCycleCreated,
}) => {
  const { toast } = useToast();

  // Estado del formulario. Usamos la forma lazy de useState para que
  // `getDefaultForm()` se invoque UNA sola vez en el montaje y no en
  // cada render, y para que `fecha` arranque con HOY local.
  const [form, setForm] = useState(() => getDefaultForm());
  const [isSaving, setIsSaving] = useState(false);

  // Estado de los últimos registros.
  // `recentAllRecords` guarda la lista completa traída del backend para
  // poder paginar en el cliente sin volver a pedir al servidor cada
  // vez que se cambia de página. `recentPage` es la página actual (1-based).
  const [recentAllRecords, setRecentAllRecords] = useState([]);
  const [recentPage, setRecentPage] = useState(1);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [highlightId, setHighlightId] = useState(null);

  const resetForm = useCallback(() => {
    setForm(getDefaultForm());
  }, []);

  // Cuando se abre el modal, resetear el formulario y cargar los últimos registros
  useEffect(() => {
    if (isOpen) {
      resetForm();
      loadRecentRecords();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleFormChange = (field, value) => {
    setForm((prev) => {
      if (field === 'type') {
        // Al cambiar de tipo, limpiamos la máquina seleccionada para evitar
        // referencias cruzadas lavadora ↔ secadora.
        return { ...prev, type: value, machineId: '' };
      }
      return { ...prev, [field]: value };
    });
  };

  /**
   * Carga los últimos ciclos abortados (lavado + secado) de la lavandería actual.
   *
   * Trae un set grande (`RECENT_FETCH_LIMIT`) de cada endpoint dedicado
   * de abortados y los mezcla en una sola lista ordenada por fecha
   * operativa descendente. La paginación se hace luego en el cliente
   * con `recentPage` para que el operador pueda navegar hacia atrás en
   * el histórico sin pedir más al servidor.
   *
   * Al finalizar vuelve a la página 1 para que el registro recién
   * creado (que siempre será el más reciente) sea visible de inmediato.
   *
   * Usamos los endpoints `/aborted/filter` (no el listado general) para
   * que el consumo del modal sea independiente de las tablas principales
   * de cada módulo.
   */
  const loadRecentRecords = useCallback(async () => {
    if (!idLavanderia) return;
    setLoadingRecent(true);
    try {
      const baseQuery = {
        'lavanderia.id': idLavanderia,
      };

      const [washingRes, dryingRes] = await Promise.allSettled([
        fetchAbortedWashingCycles({ limit: RECENT_FETCH_LIMIT, page: 1, query: baseQuery }),
        fetchAbortedDryingCycles({ limit: RECENT_FETCH_LIMIT, page: 1, query: baseQuery }),
      ]);

      const washingList =
        washingRes.status === 'fulfilled'
          ? Array.isArray(washingRes.value?.data)
            ? washingRes.value.data
            : Array.isArray(washingRes.value)
            ? washingRes.value
            : []
          : [];

      const dryingList =
        dryingRes.status === 'fulfilled'
          ? Array.isArray(dryingRes.value?.data)
            ? dryingRes.value.data
            : Array.isArray(dryingRes.value)
            ? dryingRes.value
            : []
          : [];

      const merged = [
        ...washingList.map((r) => ({ ...r, _type: 'lavado' })),
        ...dryingList.map((r) => ({ ...r, _type: 'secado' })),
      ].sort((a, b) => {
        // Ordenamos por la fecha OPERATIVA (la que el operador
        // seleccionó), no por createdAt, para que la lista refleje
        // el orden de los hechos y no el orden de los clicks.
        const da = new Date(getRecordDate(a) || 0).getTime();
        const db = new Date(getRecordDate(b) || 0).getTime();
        return db - da;
      });

      setRecentAllRecords(merged);
      setRecentPage(1);
    } catch (error) {
      console.error('Error cargando últimos ciclos abortados:', error);
      // No bloqueamos el modal por un fallo aquí, sólo dejamos la lista vacía.
      setRecentAllRecords([]);
      setRecentPage(1);
    } finally {
      setLoadingRecent(false);
    }
  }, [idLavanderia]);

  // Derivados para la paginación cliente.
  const totalRecentPages = Math.max(1, Math.ceil(recentAllRecords.length / RECENT_PAGE_SIZE));
  const recentStartIndex = (recentPage - 1) * RECENT_PAGE_SIZE;
  const recentEndIndex = recentStartIndex + RECENT_PAGE_SIZE;
  const recentVisibleRecords = useMemo(
    () => recentAllRecords.slice(recentStartIndex, recentEndIndex),
    [recentAllRecords, recentStartIndex, recentEndIndex],
  );

  // Si la página actual queda fuera de rango (por ejemplo, porque se
  // eliminaron registros en otro lugar), la corregimos automáticamente.
  useEffect(() => {
    if (recentPage > totalRecentPages) {
      setRecentPage(totalRecentPages);
    }
  }, [recentPage, totalRecentPages]);

  const handleSave = async () => {
    const isLavado = form.type === 'lavado';
    const machineList = isLavado ? machines : dryers;
    const machine = machineList.find((m) => m._id === form.machineId);

    if (!machine) {
      toast({
        title: 'Error',
        description: `Selecciona una ${isLavado ? 'lavadora' : 'secadora'}.`,
        variant: 'destructive',
      });
      return;
    }
    if (!form.ciclos || parseInt(form.ciclos, 10) <= 0) {
      toast({
        title: 'Error',
        description: 'Ingresa la cantidad de ciclos abortados.',
        variant: 'destructive',
      });
      return;
    }
    const razonFinal = form.razon === 'Otro' ? form.otraRazon?.trim() : form.razon;
    if (!razonFinal) {
      toast({
        title: 'Error',
        description: 'Selecciona o especifica la razón del abortado.',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    try {
      const dateToUse = form.fecha || getAbortedCycleDate();
      const [year, month, day] = dateToUse.split('-');
      const recordDate = new Date(Date.UTC(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10), 12, 0, 0));

      const basePayload = {
        maquina: { id: machine._id, nombre: machine.nombre },
        ciclos: parseInt(form.ciclos, 10),
        capacidad: machine.capacidad || 0,
        lavanderia: { id: idLavanderia, nombre: laundryName },
        abortado: true,
        razonAbortado: razonFinal,
        contador: machine.contador || 0,
        contadorNuevo: machine.contador || 0,
        fecha: recordDate.toISOString(),
      };

      let created;
      if (isLavado) {
        // Usamos SIEMPRE el endpoint dedicado de abortados para que la
        // fecha NO sufra el ajuste de "-1 día" que aplica el flujo
        // normal al cierre de turno. Ver `createAbortedWashingCycle`.
        created = await createAbortedWashingCycle({ ...basePayload, kilosLavados: 0 });
      } else {
        created = await createAbortedDryingCycle({ ...basePayload, kilosSecados: 0 });
      }

      toast({
        title: 'Éxito',
        description: 'Ciclos abortados registrados correctamente.',
      });

      // Resaltar el nuevo registro en la lista de la derecha
      const newRecordId = created?.data?._id || created?._id || null;
      if (newRecordId) setHighlightId(newRecordId);
      setTimeout(() => setHighlightId(null), 2500);

      // Limpiar el formulario para un nuevo registro
      resetForm();

      // Refrescar la lista de últimos registros
      await loadRecentRecords();

      // Notificar al padre (por si necesita refrescar otras métricas)
      if (onCycleCreated) onCycleCreated();
    } catch (error) {
      console.error('Error registrando ciclos abortados:', error);
      toast({
        title: 'Error',
        description: error?.message || 'Error al registrar ciclos abortados.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (isSaving) return;
    resetForm();
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent className="max-w-5xl w-[95vw] p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-3 border-b bg-muted/30">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Ban className="h-5 w-5 text-destructive" />
            Registrar Ciclos Abortados
          </DialogTitle>
          <DialogDescription>
            Registra ciclos de lavado o secado que no se completaron correctamente.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          {/* Columna izquierda: formulario */}
          <div className="p-6 border-r space-y-5">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <AlertCircle className="h-4 w-4" />
              Datos del registro
            </div>

            {/* Tipo de ciclo */}
            <div className="space-y-1.5">
              <Label htmlFor="aborted-type" className="text-sm font-medium">
                Tipo de ciclo
              </Label>
              <Select
                value={form.type}
                onValueChange={(v) => handleFormChange('type', v)}
              >
                <SelectTrigger id="aborted-type" className="h-10">
                  <SelectValue placeholder="Seleccionar tipo..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lavado">
                    <span className="flex items-center gap-2">
                      <Droplets className="h-4 w-4 text-blue-500" /> Lavado
                    </span>
                  </SelectItem>
                  <SelectItem value="secado">
                    <span className="flex items-center gap-2">
                      <Wind className="h-4 w-4 text-amber-500" /> Secado
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Máquina */}
            <div className="space-y-1.5">
              <Label htmlFor="aborted-machine" className="text-sm font-medium">
                {form.type === 'lavado' ? 'Lavadora' : 'Secadora'}
              </Label>
              <Select
                value={form.machineId}
                onValueChange={(v) => handleFormChange('machineId', v)}
              >
                <SelectTrigger id="aborted-machine" className="h-10">
                  <SelectValue
                    placeholder={
                      form.type === 'lavado'
                        ? 'Seleccionar lavadora...'
                        : 'Seleccionar secadora...'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {(form.type === 'lavado' ? machines : dryers).map((m) => (
                    <SelectItem key={m._id} value={m._id}>
                      {m.nombre}
                      {m.capacidad ? ` · ${m.capacidad} kg` : ''}
                    </SelectItem>
                  ))}
                  {form.type === 'secado' && dryers.length === 0 && (
                    <div className="px-2 py-1.5 text-xs text-muted-foreground">
                      No hay secadoras disponibles.
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Cantidad y Fecha */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="aborted-ciclos" className="text-sm font-medium">
                  Cantidad de ciclos
                </Label>
                <Input
                  id="aborted-ciclos"
                  type="number"
                  min="1"
                  value={form.ciclos}
                  onChange={(e) => handleFormChange('ciclos', e.target.value)}
                  placeholder="0"
                  className="h-10"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="aborted-fecha" className="text-sm font-medium">
                  Fecha
                </Label>
                <Input
                  id="aborted-fecha"
                  type="date"
                  value={form.fecha}
                  onChange={(e) => handleFormChange('fecha', e.target.value)}
                  // `max` se calcula también en local para que coincida
                  // exactamente con el valor por defecto del campo. Si
                  // usáramos `toISOString()` aquí, después de las 19:00
                  // (hora Colombia) el `max` quedaría apuntando al día
                  // SIGUIENTE en UTC, permitiendo seleccionar fechas
                  // futuras y desincronizándose de la fecha mostrada.
                  max={getAbortedCycleDate()}
                  className="h-10"
                />
              </div>
            </div>

            {/* Razón */}
            <div className="space-y-1.5">
              <Label htmlFor="aborted-razon" className="text-sm font-medium">
                Razón
              </Label>
              <Select
                value={form.razon}
                onValueChange={(v) => handleFormChange('razon', v)}
              >
                <SelectTrigger id="aborted-razon" className="h-10">
                  <SelectValue placeholder="Seleccionar razón..." />
                </SelectTrigger>
                <SelectContent>
                  {ABORT_REASONS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Otra razón */}
            <AnimatePresence>
              {form.razon === 'Otro' && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.18 }}
                  className="space-y-1.5 overflow-hidden"
                >
                  <Label htmlFor="aborted-otra" className="text-sm font-medium">
                    Especificar razón
                  </Label>
                  <Input
                    id="aborted-otra"
                    placeholder="Describe la razón..."
                    value={form.otraRazon}
                    onChange={(e) => handleFormChange('otraRazon', e.target.value)}
                    className="h-10"
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Columna derecha: últimos registros */}
          <div className="p-6 bg-muted/20 flex flex-col min-h-[300px] max-h-[70vh]">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <History className="h-4 w-4" />
                Últimos ciclos abortados
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={loadRecentRecords}
                disabled={loadingRecent}
                className="h-7 px-2 text-xs"
                title="Refrescar lista"
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loadingRecent ? 'animate-spin' : ''}`} />
                Refrescar
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto pr-1 -mr-1 space-y-2">
              {loadingRecent && recentAllRecords.length === 0 && (
                <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Cargando últimos registros...
                </div>
              )}

              {!loadingRecent && recentAllRecords.length === 0 && (
                <div className="flex flex-col items-center justify-center py-10 text-center text-sm text-muted-foreground border border-dashed rounded-md">
                  <CheckCircle2 className="h-8 w-8 mb-2 text-emerald-500" />
                  <p className="font-medium text-foreground">Sin ciclos abortados</p>
                  <p className="text-xs mt-1">
                    Cuando registres uno aparecerá aquí para confirmación visual.
                  </p>
                </div>
              )}

              <AnimatePresence initial={false}>
                {recentVisibleRecords.map((r) => {
                  const id = r._id || `${r._type}-${getRecordDate(r)}`;
                  const isHighlighted = highlightId && r._id === highlightId;
                  return (
                    <motion.div
                      key={id}
                      layout
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.18 }}
                      className={`rounded-md border p-3 bg-background shadow-sm transition-colors ${
                        isHighlighted
                          ? 'border-emerald-400 ring-2 ring-emerald-300/50 bg-emerald-50/40'
                          : 'border-border'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {r._type === 'lavado' ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-700 px-2 py-0.5 text-[11px] font-medium">
                              <Droplets className="h-3 w-3" /> Lavado
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-[11px] font-medium">
                              <Wind className="h-3 w-3" /> Secado
                            </span>
                          )}
                          <span className="text-sm font-semibold truncate">
                            {r.maquina?.nombre || 'Máquina'}
                          </span>
                          {isHighlighted && (
                            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 font-medium">
                              <CheckCircle2 className="h-3 w-3" /> Recién creado
                            </span>
                          )}
                        </div>
                        {/* Mostramos SIEMPRE la fecha OPERATIVA del
                            ciclo (la que el operador seleccionó al
                            registrarlo), no la fecha de creación del
                            registro. Si el operador registró "ayer"
                            en la mañana, la tarjeta muestra "ayer" —
                            que es lo que tiene sentido para él. */}
                        <span
                          className="text-[11px] text-muted-foreground whitespace-nowrap"
                          title="Fecha del ciclo (la que registraste)"
                        >
                          {formatDateTime(getRecordDate(r))}
                        </span>
                      </div>
                      <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                        <div className="flex justify-between col-span-1">
                          <span className="text-muted-foreground">Ciclos:</span>
                          <span className="font-medium">{r.ciclos ?? 0}</span>
                        </div>
                        <div className="flex justify-between col-span-1">
                          <span className="text-muted-foreground">Capacidad:</span>
                          <span className="font-medium">{r.capacidad ?? 0} kg</span>
                        </div>
                      </div>
                      {r.razonAbortado && (
                        <div className="mt-1.5 text-xs">
                          <span className="text-muted-foreground">Razón: </span>
                          <span className="font-medium">{r.razonAbortado}</span>
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>

            {/* Paginador + contador de la lista de la derecha */}
            <div className="mt-3 pt-3 border-t border-border/60 flex flex-col gap-2">
              {recentAllRecords.length > 0 && (
                <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {recentAllRecords.length === 0
                      ? 'Sin registros'
                      : `${recentStartIndex + 1}–${Math.min(
                          recentEndIndex,
                          recentAllRecords.length,
                        )} de ${recentAllRecords.length}`}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => setRecentPage(1)}
                      disabled={recentPage <= 1 || loadingRecent}
                      title="Primera página"
                    >
                      <ChevronsLeft className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => setRecentPage((p) => Math.max(1, p - 1))}
                      disabled={recentPage <= 1 || loadingRecent}
                      title="Anterior"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </Button>
                    <span className="px-1.5 text-[11px] font-medium text-foreground min-w-[58px] text-center">
                      {recentPage} / {totalRecentPages}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => setRecentPage((p) => Math.min(totalRecentPages, p + 1))}
                      disabled={recentPage >= totalRecentPages || loadingRecent}
                      title="Siguiente"
                    >
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => setRecentPage(totalRecentPages)}
                      disabled={recentPage >= totalRecentPages || loadingRecent}
                      title="Última página"
                    >
                      <ChevronsRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}
              <p className="text-[10px] text-muted-foreground/80">
                Histórico de ciclos abortados (lavado + secado) de este centro. Usa las flechas para navegar.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t bg-muted/30">
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Ban className="mr-2 h-4 w-4" />
            )}
            Registrar ciclos abortados
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AbortedCyclesModal;
